import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import ClauseCard from '../../_containers/ClauseCard';
import ContractPreviewCard from './components/ContractPreviewCard';
import CompletionModal, { AccountFormData } from '../contract-analysis/components/CompletionModal';
import axios from '../../lib/axios';
import { setUserProperties, track } from '../../lib/mixpanel';
import { useUserStore } from '../../store/userStore';
import { FileText } from 'lucide-react';
import FloatingActionButton from '../../components/ui/FloatingActionButton';

interface ContractPreviewData {
  id?: string;
  userServiceId?: string;
  fileName: string;
  fileSize: string;
  pageCount: number;
  previewText: string;
  detectedType: string;
  contractType: string;
  contractSubType: string;
  contractTitle: string;
  defaultClauses: string;
  contradictionAnalysis: string;
  equilibriumAnalysis: string;
  analysisEstimate: number; // in seconds
  exoticClauses: string;
  finalScore: { resumo_contrato: string; score: string };
  objectiveAnalysis: string;
  keyValuesAnalysis: string;
  importantDatesAnalysis: string;
}

const ContractPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [previewData, setPreviewData] = useState<ContractPreviewData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [selectedClause, setSelectedClause] = useState<string>('');
  const { user, registerAccount } = useUserStore();

  useEffect(() => {
    console.log(location.state, 'state');
    if (!location.state || !location.state.analysisData) {
      navigate('/');
    } else {
      setPreviewData(location.state.analysisData);

      track('Score Viewed', {
        score: location.state.finalScore,
        document_id: location.state.id,
      });
    }
  }, [location, navigate]);

  const handleClauseClick = (clauseTitle: string) => {
    setSelectedClause(clauseTitle);
    setShowModal(true);

    track('Details Button Clicked', {
      clause_title: clauseTitle,
      user_id: user,
    });
  };

  const handleCompleteRegistration = async (userData: AccountFormData) => {
    setSubmittingForm(true);
    try {
      // Usar a função registerAccount do userStore
      await registerAccount({
        ...userData,
        userServiceId: previewData?.userServiceId || '',
      });

      trackMixpanel(userData);
      // Após atualizar os dados do usuário, verificamos se temos o userServiceId
      if (previewData?.userServiceId) {
        try {
          // Fazer a chamada GET para obter os resultados detalhados da análise
          const { data } = await axios.get(`/services/score/results/${previewData.userServiceId}`);

          // Se a chamada for bem-sucedida, atualizamos os dados de análise com os resultados detalhados
          if (data) {
            console.log('Resultados detalhados obtidos:', data);

            // Navegar para a página de resultados detalhados da cláusula com os dados atualizados
            navigate('/contract-analysis/results', { state: { analysisData: data } });
            return; // Saímos da função para não executar a navegação abaixo
          }
        } catch (resultError) {
          console.error('Erro ao obter resultados detalhados:', resultError);
          // Continuamos com a navegação normal em caso de erro
        }
      }

      // Navegação padrão com os dados que já temos
      navigate('/results', {
        state: {
          clauseData: selectedClause,
          analysisData: previewData,
        },
      });
    } catch (err) {
      console.error('Erro ao processar solicitação:', err);
    } finally {
      setSubmittingForm(false);
      setShowModal(false);
    }
  };

  const trackMixpanel = (userData: AccountFormData) => {
    track('Lead Qualification Completed', {
      email: userData.email,
      name: userData.name,
      company_name: userData.companyName,
      company_size: userData.employeesNumber,
      industry: userData.businessSegment,
      phone: userData.cellphone,
      user_id: user?.id,
    });

    setUserProperties({
      $email: userData.email,
      $name: userData.name,
      company_name: userData.companyName,
      company_size: userData.employeesNumber,
      industry: userData.businessSegment,
      $phone: userData.cellphone,
      qualified_date: new Date(),
      status: 'qualified_lead',
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header rightLink={{ text: 'Cancelar', to: '/' }} />

      <main className="flex-grow flex flex-col items-center py-8">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Resultado da Análise</h2>
          </motion.div>

          <ContractPreviewCard
            fileName={previewData?.fileName || ''}
            fileSize={previewData?.fileSize || ''}
            pageCount={previewData?.pageCount || 0}
            contractType={previewData?.contractType || ''}
            contractSubType={previewData?.contractSubType || ''}
            contractTitle={previewData?.contractTitle || ''}
            previewText={previewData?.finalScore?.resumo_contrato || ''}
            score={previewData?.finalScore?.score || 0}
            fabButton={true}
            onFabClick={handleClauseClick}
          />

          <ClauseCard
            title="Contradição"
            tooltip="Identifica conflitos ou inconsistências internas no contrato que possam gerar confusão ou disputas jurídicas."
            score={previewData?.contradictionAnalysis}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Riscos financeiros"
            tooltip="Avalia penalidades, multas e condições financeiras para garantir que não haja riscos financeiros desproporcionais ou injustos."
            score={previewData?.keyValuesAnalysis}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Datas importantes"
            tooltip="Verifica se todas as datas e prazos importantes estão claramente definidos, evitando ambiguidades ou falta de informações essenciais."
            score={previewData?.importantDatesAnalysis}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Equilíbrio contratual"
            tooltip="Verifica se o contrato é justo para ambas as partes, sem cláusulas desproporcionais ou que favoreçam excessivamente uma das partes."
            score={previewData?.equilibriumAnalysis}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Objetividade, erros e repetições"
            tooltip="Avalia a clareza do texto, detectando erros de digitação, ambiguidade e repetições desnecessárias que podem prejudicar a compreensão."
            score={previewData?.objectiveAnalysis}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Clausulas padrões"
            tooltip="Verifica se seu contrato contém todas as cláusulas essenciais e típicas para o tipo de contrato em questão."
            score={previewData?.defaultClauses || 0}
            onArrowClick={handleClauseClick}
          />

          <ClauseCard
            title="Cláusulas atípicas"
            tooltip="Identifica cláusulas incomuns ou desnecessárias, que não são típicas para o tipo de contrato analisado."
            onArrowClick={handleClauseClick}
            badgeType="status"
            score={previewData?.exoticClauses}
          />
        </div>
      </main>

      {/* Modal para completar cadastro */}
      <CompletionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCompleteRegistration}
        loading={submittingForm}
      />
      <FloatingActionButton
        onClick={() => handleClauseClick('Deixe seu feedback')}
        label="Complete seu cadastro"
        icon={<FileText />}
      />
    </div>
  );
};

export default ContractPreview;
